import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getLocale, useIntl } from 'umi';
import { useLocal, useOption, useWeekDay, useWeekDayArr } from '@/pages/dashboard/utils/customHook';
import { handleAxisFormat, handleDownload } from '@/pages/dashboard/utils/utils';
import CardBox from '@/pages/dashboard/components/CardBox';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { xAxisHighLight, xAxisLabelFormat } from '@/pages/dashboard/components/Chart/_config/plug';
import { chartTooltiosFormatter } from '@/pages/dashboard/components/Chart/_config/utils';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { LANGUAGE_EN } from '@/pages/dashboard/_config/constant';
import { EChartsOption } from 'echarts';
import { SegmentOption } from 'tea-component/lib/segment/SegmentOption';
import classNames from 'classnames';
import './index.scss';

interface IProps {
  // 英文标题
  titleEn?: string;
  // 中文标题
  titleCn?: string;
  subTitleEn?: string;
  subTitleCn?: string;
  className?: string;
  style?: React.CSSProperties;
  // format后的数据
  data?: {
    data: {
      data: {
        chartData: string[];
        tableData: string[];
      };
      loading: boolean;
      errorMsg: string;
    };
    metric:string;
    headerTotalEn:string;
    headerTotalCn:string;
  };
  // 表格表头配置
  options?: {[key:string]: string;} [];
  componentSegment?: SegmentOption[];
  changeData?: (key: string, value: string|boolean|number) => void;
  defaultActiveTab:number;
  hasSum:boolean;
  height:number;
}

export default function CreatorProfile(props: IProps) {
  const { titleEn = '', titleCn = '', subTitleEn = '', subTitleCn = '', className = '', style = {}, options, componentSegment, changeData, data, defaultActiveTab = 1, hasSum=false, height=290} = props || {};
  const { data: propsData, loading = true, errorMsg: error='' } = data?.data || {};
  const contextObj = useContext(FilterContext);
  const { filterVal } = contextObj || {};
  const { timeType = 'Day' } = filterVal || {};
  const isEn = getLocale() === LANGUAGE_EN;

  // 获取周展示相关信息
  const { getWeekDay } = useWeekDay();
  const weekDayArr = useWeekDayArr();
  const { formatMessage } = useIntl(); // 中英切换方法
  const barChartRef = useRef<any>();
  const lineChartRef = useRef<any>();
  const [iseye, setIseye] = useState(true);
  const { isEN } = useLocal();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedSegment, setSelectedSegment] = useState(componentSegment?.[0]?.value || '');

  // 防止切换中英文，浮窗不会同步更新,用key处理会造成整个图表重新渲染
  useEffect(() => {
    barChartRef.current?.refreshChart();
    lineChartRef.current?.refreshChart();
  }, [isEn]);
  
  // 标题相关
  const { title, subTitle } = useMemo(() => ({
    title: (isEn && titleEn) ? titleEn : titleCn,
    subTitle: (isEn && subTitleEn) ? subTitleEn : subTitleCn
  }), [titleEn, titleCn, subTitleEn, subTitleCn, isEn]);

  

  const SegmenOptions = useMemo(() => componentSegment?.map((item: any) => {
    const { enName, cnName, value} = item;
    return {
      text: isEN ? enName : cnName,
      value
    };
  }), [componentSegment, isEN]);

  const downloadTitle = selectedSegment 
    ? `${title}_${SegmenOptions?.find(item => item.value === selectedSegment)?.text}` 
    : title;


  const headerTotal = useMemo(() => {
    const foundItem = componentSegment?.find((item: any) => {
      const { value } = item;
      return value === data?.metric;
    });
    return foundItem ? foundItem.cnName : null;
  }, [componentSegment, data?.metric]);

  // 渲染数据，图和表格
  const componentData = useMemo(() => {
    // 动态生成表头
    const tableOptions = options?.map((item: {[key:string]: string;})=>{
      const header = (isEn && item?.headerEn) ? item?.headerEn : item?.headerCn;
      if(item?.key === 'date') {
        return {
          ...(item || {}),
          header,
          render: (cvm:{[key:string]: string;}) => {
            const weekDay = getWeekDay(cvm?.date);
            const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
            return <>
              <span className='table-text'>{cvm?.date}</span>
              {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
            </>;
          }
        };
      }
      return {
        ...(item || {}),
        header: item?.key === 'total' ? headerTotal : header,
        render: (cvm: any) => (<span>{cvm?.[item?.key] || '-'}</span>),
      };
    });
    return {
      chart: propsData?.chartData || [],
      table: {
        data: propsData?.tableData || [],
        option: tableOptions
      },
    };
  }, [propsData, options, isEn]);

  const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(componentData);

  // echart 图插件功能展示
  const commonChartOtherConfig = useCallback((opt: EChartsOption)=>{
    let tempOpt = opt;
    tempOpt = xAxisLabelFormat(tempOpt, { xAxisLabelFormat: (value: string)=>handleAxisFormat(value, timeType) });
    tempOpt = xAxisHighLight(tempOpt, {
      formatter: (params: { value: string; }) => handleAxisFormat(params?.value, timeType)
    });
    return tempOpt;
  },[timeType]);

  // 渲染列表
  const renderList = useMemo(()=>[
    {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <BarChart
        ref={barChartRef}
        height={height}
        legendData={xAxisData?.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData?.length > 0 ? barSeries : []}
        formatterFunc={(params: any[]) => chartTooltiosFormatter(params, { weekDayArr, sumName: headerTotal, hasSum})}
        isStackBar={iseye}
        setOtherConfigFunc={commonChartOtherConfig}
      />
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: <LineChart
        ref={lineChartRef}
        height={height}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? lineSeries : []}
        formatterFunc={(params: any[]) => chartTooltiosFormatter(params, { weekDayArr,sumName: headerTotal,hasSum: data?.hasSum})}
        setOtherConfigFunc={commonChartOtherConfig}
      />
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: <MiniTable
        height={height}
        loading={false}
        tableOption={componentData?.table?.option}
        tableList={componentData?.table?.data}
      />
    },
  ],[ componentData, commonChartOtherConfig, weekDayArr, iseye]);

  return (
    <CardBox
      className={classNames(className,'creator-profile-container')}
      style={style}
      height={componentSegment && isNodata?height+30:height}
      bordered={false}
      titleCom={title}
      segmentOption={SegmenOptions}
      selectedSegment={selectedSegment}
      setSelectedSegment={setSelectedSegment}
      setCurrentDataType={(type: any) => {
        if (type === 'eyes') {
          setIseye(!iseye);
        }
      }}
      subTitleCom={subTitle}
      renderList={renderList}
      defaultAvtive={defaultActiveTab}
      downloadClick={() => {
        handleDownload(componentData?.table, downloadTitle);
      }}
      isLoading={loading}
      isError={!!error}
      isNodata={isNodata}
      changeData={changeData}
      dataKey={'metric'}
    />
  );
}
